/**
 * WebSocketProvider — Singleton WebSocket connection shared across all components.
 *
 * Problem: Previously, each call to useRealtimeUpdates() created a new WebSocket connection.
 * With 4+ components using the hook, this meant 4+ simultaneous connections per user.
 *
 * Solution: A single WebSocket connection managed via React Context, mounted in DashboardLayout.
 * All components consume the shared connection state via useWebSocketContext() hook.
 */

import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/auth'
import { useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { toast } from 'sonner'

import { balanceKeys } from '@/hooks/use-balance'
import { billingKeys } from '@/hooks/use-billing'
import { domainKeys } from '@/hooks/use-domains'
import { notificationKeys } from '@/hooks/use-notifications-api'
import { useNotificationStore } from '@/store/notifications'

import type { ConnectionState, WebSocketContextValue, WebSocketMessage } from './websocket-types'
import { WebSocketContext } from './websocket-types'

const MAX_RECONNECT_ATTEMPTS = 5
const BASE_RECONNECT_MS = 2000 // Initial delay, doubles each attempt (2s, 4s, 8s, 16s, 32s)
const HEARTBEAT_INTERVAL_MS = 25000 // Send ping every 25s (server timeout is 90s)

/**
 * Refresh the access token before WebSocket reconnection.
 * Returns the new (or current) token, or null if refresh failed.
 *
 * IMPORTANT: This must NEVER call logout().  WebSocket disconnects are
 * transient and should not cascade into a global session termination.
 * The normal API 401 interceptor in client.ts handles real session expiry.
 */
async function refreshAccessToken(): Promise<string | null> {
  const { token, refreshToken, tokenExpiresAt, setTokens } = useAuthStore.getState()

  // If the current access token is still valid (>60 s left), skip the refresh
  // entirely — the disconnect was likely a transient network issue.
  if (token && tokenExpiresAt) {
    const nowSec = Math.floor(Date.now() / 1000)
    if (tokenExpiresAt - nowSec > 60) {
      return token
    }
  }

  if (!refreshToken) {
    return token ?? null
  }

  // Check if another tab already refreshed by reading localStorage directly.
  try {
    const raw = localStorage.getItem('ghostflow-auth')
    if (raw) {
      const persisted = JSON.parse(raw) as { state?: { token?: string; tokenExpiresAt?: number } }
      const pToken = persisted?.state?.token
      const pExp = persisted?.state?.tokenExpiresAt
      if (pToken && pToken !== token && pExp) {
        const nowSec = Math.floor(Date.now() / 1000)
        if (pExp - nowSec > 60) {
          // Another tab refreshed — adopt the new token without making a request
          useAuthStore.setState({ token: pToken })
          return pToken
        }
      }
    }
  } catch {
    // Ignore localStorage read errors
  }

  try {
    const apiUrl = import.meta.env.VITE_API_URL || ''
    const response = await fetch(`${apiUrl}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      cache: 'no-store',
    })

    if (!response.ok) {
      // Don't logout — let the normal API 401 flow handle session expiry.
      return null
    }

    const data = await response.json() as { accessToken: string; refreshToken: string; accessTokenExpiresAt?: number }
    setTokens(data.accessToken, data.refreshToken, data.accessTokenExpiresAt)
    return data.accessToken
  } catch {
    console.warn('Token refresh failed during WebSocket reconnect')
    return null
  }
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const isDevelopment = import.meta.env.DEV

  const wsRef = React.useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = React.useRef(0)
  const reconnectTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const hasShownErrorRef = React.useRef(false)

  const [connectionState, setConnectionState] = React.useState<ConnectionState>('disconnected')
  const [lastMessage, setLastMessage] = React.useState<WebSocketMessage | null>(null)

  const getWsUrl = React.useCallback((): string => {
    const token = useAuthStore.getState().token || (isDevelopment ? 'dev-token' : '')
    const baseUrl = import.meta.env.VITE_WS_URL || '/ws'

    if (baseUrl.startsWith('/')) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${protocol}//${window.location.host}${baseUrl}?token=${encodeURIComponent(token)}`
    }

    const separator = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${separator}token=${encodeURIComponent(token)}`
  }, [isDevelopment])

  /**
   * Handle incoming WebSocket messages.
   *
   * Event type names match backend serde tags exactly:
   *   Backend (Rust)         → Frontend (JS)
   *   "click.new"            → 'click.new'
   *   "conversion.new"       → 'conversion.new'
   *   "stats.update"         → 'stats.update'
   *   "alert.new"            → 'alert.new'
   *   "access.changed"       → 'access.changed'
   *   "member.role_changed"  → 'member.role_changed'
   *   "member.removed"       → 'member.removed'
   *   "progress.update"      → 'progress.update'
   *   "welcome"              → 'welcome'
   */
  const handleMessage = React.useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      case 'welcome':
        console.log('WebSocket connected, server version:', (message.payload as { serverVersion?: string })?.serverVersion)
        break

      case 'click.new': {
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['campaigns'] })
        break
      }

      case 'conversion.new': {
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        queryClient.invalidateQueries({ queryKey: ['campaigns'] })
        // Backend ConversionEventData (camelCase): { clickId, campaignId, status, payout, currency, timestamp }
        const convPayload = message.payload as {
          campaignId?: string
          payout?: number
          currency?: string
        }
        const revenueStr = convPayload.payout
          ? ` — ${convPayload.currency === 'USD' ? '$' : ''}${convPayload.payout.toFixed(2)}`
          : ''
        toast.success('New Conversion!', {
          description: `Campaign${revenueStr}`,
          duration: 5000,
        })
        break
      }

      case 'campaign_update':
        queryClient.invalidateQueries({ queryKey: ['campaigns'] })
        break

      case 'offer_update':
        queryClient.invalidateQueries({ queryKey: ['offers'] })
        break

      case 'landing_update':
        queryClient.invalidateQueries({ queryKey: ['landings'] })
        break

      case 'source_update':
        queryClient.invalidateQueries({ queryKey: ['sources'] })
        break

      case 'access.changed': {
        const accessPayload = message.payload as {
          teamId?: string
          userId?: string
          changedBy?: string
        }
        queryClient.invalidateQueries({ queryKey: ['access'] })
        const currentUserId = useAuthStore.getState().user?.id
        if (accessPayload.userId === currentUserId) {
          toast.info('Access Updated', {
            description: 'Your access permissions have been updated by team admin.',
            duration: 6000,
          })
        }
        break
      }

      case 'member.role_changed': {
        const rolePayload = message.payload as {
          teamId?: string
          userId?: string
          newRole?: string
        }
        queryClient.invalidateQueries({ queryKey: ['access'] })
        queryClient.invalidateQueries({ queryKey: ['team'] })
        const curUserId = useAuthStore.getState().user?.id
        if (rolePayload.userId === curUserId) {
          toast.info('Role Changed', {
            description: `Your role has been changed to ${rolePayload.newRole || 'unknown'}.`,
            duration: 6000,
          })
        }
        break
      }

      case 'member.removed': {
        const removedPayload = message.payload as {
          teamId?: string
          teamName?: string
          userId?: string
        }
        const myUserId = useAuthStore.getState().user?.id
        if (removedPayload.userId === myUserId) {
          queryClient.invalidateQueries({ queryKey: ['access'] })
          queryClient.invalidateQueries({ queryKey: ['team'] })
          toast.warning('Team Access Revoked', {
            description: `You have been removed from ${removedPayload.teamName || 'the team'}.`,
            duration: 8000,
          })
          setTimeout(() => {
            window.location.href = '/dashboard'
          }, 2000)
        }
        break
      }

      case 'alert.new': {
        const alertPayload = message.payload as {
          level?: 'info' | 'warning' | 'error' | 'critical'
          title?: string
          message?: string
        }
        const level = alertPayload.level || 'info'
        const title = alertPayload.title || 'Alert'
        const desc = alertPayload.message

        if (level === 'error' || level === 'critical') {
          toast.error(title, { description: desc, duration: 8000 })
        } else if (level === 'warning') {
          toast.warning(title, { description: desc, duration: 6000 })
        } else {
          toast.info(title, { description: desc, duration: 4000 })
        }
        break
      }

      case 'stats.update':
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        break

      case 'progress.update':
        queryClient.invalidateQueries({ queryKey: ['progress'] })
        break

      case 'pong':
      case 'subscribed':
      case 'unsubscribed':
        break

      case 'error': {
        const errorPayload = message.payload as { code?: number; message?: string }
        console.error('WebSocket server error:', errorPayload.code, errorPayload.message)
        break
      }

      // ═══════════════════════════════════════════════════════════════════
      // Billing / Balance events (real-time push from billing service)
      // ═══════════════════════════════════════════════════════════════════
      case 'billing.balance_updated': {
        queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
        queryClient.invalidateQueries({ queryKey: balanceKeys.transactions() })
        queryClient.invalidateQueries({ queryKey: billingKeys.overview() })
        const balPayload = message.payload as { balanceCents?: number; changeCents?: number; txType?: string; description?: string }
        const sign = (balPayload.changeCents ?? 0) >= 0 ? '+' : ''
        const usd = ((balPayload.changeCents ?? 0) / 100).toFixed(2)
        toast.info('Balance updated', {
          description: `${sign}$${usd} — ${balPayload.description || balPayload.txType || 'Balance change'}`,
          duration: 5000,
        })
        break
      }
      case 'billing.topup_created': {
        queryClient.invalidateQueries({ queryKey: balanceKeys.activeTopUps() })
        break
      }
      case 'billing.topup_confirming': {
        const tcPayload = message.payload as { sessionId?: string }
        if (tcPayload.sessionId) {
          queryClient.invalidateQueries({ queryKey: balanceKeys.topUpSession(tcPayload.sessionId) })
        }
        queryClient.invalidateQueries({ queryKey: balanceKeys.activeTopUps() })
        // Dispatch custom event for TopUpFlow component to react immediately
        window.dispatchEvent(new CustomEvent('ghostflow:billing', { detail: message }))
        toast.info('Payment detected!', {
          description: 'Your crypto payment is being confirmed on the blockchain.',
          duration: 6000,
        })
        break
      }
      case 'billing.topup_completed': {
        queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
        queryClient.invalidateQueries({ queryKey: balanceKeys.transactions() })
        queryClient.invalidateQueries({ queryKey: balanceKeys.activeTopUps() })
        queryClient.invalidateQueries({ queryKey: billingKeys.overview() })
        const tcDonePayload = message.payload as { amountCents?: number; sessionId?: string }
        if (tcDonePayload.sessionId) {
          queryClient.invalidateQueries({ queryKey: balanceKeys.topUpSession(tcDonePayload.sessionId) })
        }
        // Dispatch custom event for TopUpFlow component
        window.dispatchEvent(new CustomEvent('ghostflow:billing', { detail: message }))
        const topupUsd = ((tcDonePayload.amountCents ?? 0) / 100).toFixed(2)
        toast.success(`Top-up of $${topupUsd} completed!`, { duration: 6000 })
        break
      }
      case 'billing.topup_failed': {
        const tfPayload = message.payload as { sessionId?: string; status?: string }
        if (tfPayload.sessionId) {
          queryClient.invalidateQueries({ queryKey: balanceKeys.topUpSession(tfPayload.sessionId) })
        }
        queryClient.invalidateQueries({ queryKey: balanceKeys.activeTopUps() })
        // Dispatch custom event for TopUpFlow component
        window.dispatchEvent(new CustomEvent('ghostflow:billing', { detail: message }))
        const isExpired = tfPayload.status === 'expired'
        if (isExpired) {
          toast.warning('Top-up session expired', {
            description: 'The payment window has expired. You can try again.',
            duration: 6000,
          })
        } else {
          toast.error('Top-up payment failed', {
            description: 'Please try again or contact support.',
            duration: 8000,
          })
        }
        break
      }
      case 'billing.payment_failed': {
        queryClient.invalidateQueries({ queryKey: billingKeys.subscription() })
        toast.warning('Subscription payment failed', {
          description: 'Please top up your balance to continue your subscription.',
          duration: 10000,
        })
        break
      }
      case 'billing.subscription_activated': {
        queryClient.invalidateQueries({ queryKey: billingKeys.all })
        queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
        // Refresh user profile to pick up the new plan
        authApi.me().then((user) => {
          useAuthStore.getState().updateUser(user)
        }).catch(() => { /* ignore — will refresh on next navigation */ })
        toast.success('Subscription activated!', { duration: 5000 })
        break
      }
      case 'billing.subscription_upgraded': {
        queryClient.invalidateQueries({ queryKey: billingKeys.all })
        queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
        // Refresh user profile to pick up the new plan
        authApi.me().then((user) => {
          useAuthStore.getState().updateUser(user)
        }).catch(() => { /* ignore */ })
        toast.success('Subscription upgraded!', { duration: 5000 })
        break
      }
      case 'billing.subscription_cancelled': {
        queryClient.invalidateQueries({ queryKey: billingKeys.all })
        // Refresh user profile to reflect plan change
        authApi.me().then((user) => {
          useAuthStore.getState().updateUser(user)
        }).catch(() => { /* ignore */ })
        toast.info('Subscription cancelled', { duration: 5000 })
        break
      }
      case 'billing.subscription_suspended': {
        queryClient.invalidateQueries({ queryKey: billingKeys.subscription() })
        toast.warning('Subscription suspended', {
          description: 'Your subscription has been suspended due to non-payment.',
          duration: 10000,
        })
        break
      }
      case 'billing.trial_ending': {
        const trialPayload = message.payload as { daysRemaining?: number }
        toast.warning('Trial ending soon', {
          description: `Your trial ends in ${trialPayload.daysRemaining ?? '?'} days. Add funds to continue.`,
          duration: 10000,
        })
        break
      }
      case 'billing.service_payment_completed': {
        queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
        queryClient.invalidateQueries({ queryKey: balanceKeys.servicePayments() })
        queryClient.invalidateQueries({ queryKey: balanceKeys.transactions() })
        toast.success('Service payment completed', { duration: 5000 })
        break
      }

      // ═══════════════════════════════════════════════════════════════════
      // Domain events
      // ═══════════════════════════════════════════════════════════════════
      case 'domain.purchase_completed': {
        queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
        queryClient.invalidateQueries({ queryKey: domainKeys.registrations() })
        queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
        const dpPayload = message.payload as { domain?: string }
        toast.success('Domain purchased!', {
          description: dpPayload.domain ?? 'Domain registration complete.',
          duration: 6000,
        })
        window.dispatchEvent(new CustomEvent('ghostflow:ws', { detail: message }))
        break
      }
      case 'domain.purchase_failed': {
        const dpfPayload = message.payload as { domain?: string; error?: string }
        toast.error(`Domain purchase failed: ${dpfPayload.domain ?? ''}`, {
          description: dpfPayload.error ?? 'Please try again or contact support.',
          duration: 8000,
        })
        window.dispatchEvent(new CustomEvent('ghostflow:ws', { detail: message }))
        break
      }
      case 'domain.renewal_completed': {
        queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
        queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
        const drPayload = message.payload as { domain?: string }
        toast.success(`Domain renewed: ${drPayload.domain ?? ''}`, { duration: 5000 })
        break
      }
      case 'domain.cf_setup_progress':
      case 'domain.cf_setup_completed':
      case 'domain.cf_setup_failed':
      case 'domain.ns_updated':
      case 'domain.ns_update_failed':
      case 'domain.ssl_activated':
      case 'domain.ssl_pending_warning': {
        queryClient.invalidateQueries({ queryKey: domainKeys.lists() })
        window.dispatchEvent(new CustomEvent('ghostflow:ws', { detail: message }))
        break
      }

      // ═══════════════════════════════════════════════════════════════════
      // AI Content Generation events (Chameleon)
      // ═══════════════════════════════════════════════════════════════════
      case 'ai.generation_started': {
        const rawStartPayload = message.payload as { data?: { generationType?: string; operationId?: string }; generationType?: string; operationId?: string }
        const generationType = rawStartPayload.data?.generationType ?? rawStartPayload.generationType
        const startOpId = rawStartPayload.data?.operationId ?? rawStartPayload.operationId
        toast.info('AI generating...', {
          id: `ai-started-${startOpId || generationType || 'content'}`,
          description: `Generating ${generationType || 'content'}`,
          duration: 3000,
        })
        break
      }
      case 'ai.generation_completed': {
        const rawDonePayload = message.payload as {
          data?: { generationType?: string; tokensUsed?: number; remainingRequests?: number; operationId?: string }
          generationType?: string
          tokensUsed?: number
          remainingRequests?: number
          operationId?: string
        }
        const aiDonePayload = rawDonePayload.data ?? rawDonePayload
        toast.success('AI generation complete', {
          id: `ai-completed-${aiDonePayload.operationId || aiDonePayload.generationType || 'content'}`,
          description: `${aiDonePayload.generationType || 'Content'} ready${aiDonePayload.remainingRequests != null ? ` — ${aiDonePayload.remainingRequests} requests left` : ''}`,
          duration: 4000,
        })
        // Notify per-operation listeners (e.g. AutoGenerateTab awaiting a
        // specific background job) without coupling this global handler to
        // any single component — mirrors the domain/billing event pattern.
        window.dispatchEvent(new CustomEvent('ghostflow:ws', { detail: message }))
        break
      }
      case 'ai.generation_failed': {
        const rawFailPayload = message.payload as {
          data?: { generationType?: string; error?: string; operationId?: string }
          generationType?: string
          error?: string
          operationId?: string
        }
        const aiFailPayload = rawFailPayload.data ?? rawFailPayload
        toast.error('AI generation failed', {
          id: `ai-failed-${aiFailPayload.operationId || aiFailPayload.generationType || 'content'}`,
          description: aiFailPayload.error || `Failed to generate ${aiFailPayload.generationType || 'content'}`,
          duration: 6000,
        })
        window.dispatchEvent(new CustomEvent('ghostflow:ws', { detail: message }))
        break
      }

      // ═══════════════════════════════════════════════════════════════════
      // Affiliate events (partner / referral program)
      // ═══════════════════════════════════════════════════════════════════
      case 'affiliate.commission_earned': {
        queryClient.invalidateQueries({ queryKey: ['affiliate'] })
        const aePayload = ((message.payload as { data?: Record<string, unknown> })?.data ?? message.payload) as {
          amountCents?: number; details?: Record<string, string>
        }
        const amountStr = aePayload.amountCents != null ? ` $${(aePayload.amountCents / 100).toFixed(2)}` : ''
        toast.success('Commission earned', {
          description: `You earned${amountStr} from a referral`,
          duration: 5000,
        })
        break
      }
      case 'affiliate.tier_upgraded': {
        queryClient.invalidateQueries({ queryKey: ['affiliate'] })
        const tuPayload = ((message.payload as { data?: Record<string, string> })?.data ?? message.payload) as {
          details?: Record<string, string>
        }
        const tierName = tuPayload.details?.tier ?? 'a new tier'
        toast.success('Tier upgraded!', {
          description: `You've been promoted to ${tierName}`,
          duration: 5000,
        })
        break
      }
      case 'affiliate.payout_completed': {
        queryClient.invalidateQueries({ queryKey: ['affiliate'] })
        const pcPayload = ((message.payload as { data?: Record<string, unknown> })?.data ?? message.payload) as {
          amountCents?: number
        }
        const payoutStr = pcPayload.amountCents != null ? ` of $${(pcPayload.amountCents / 100).toFixed(2)}` : ''
        toast.success('Payout completed', {
          description: `Your payout${payoutStr} has been processed`,
          duration: 5000,
        })
        break
      }
      case 'affiliate.referral_signup': {
        queryClient.invalidateQueries({ queryKey: ['affiliate'] })
        toast.info('New referral!', {
          description: 'A new user signed up via your referral link',
          duration: 4000,
        })
        break
      }
      case 'affiliate.commission_reversed': {
        queryClient.invalidateQueries({ queryKey: ['affiliate'] })
        const crPayload = ((message.payload as { data?: Record<string, unknown> })?.data ?? message.payload) as {
          amountCents?: number; details?: Record<string, string>
        }
        const reversedStr = crPayload.amountCents != null ? ` $${(crPayload.amountCents / 100).toFixed(2)}` : ''
        const reason = crPayload.details?.reason ?? 'policy violation'
        toast.warning('Commission reversed', {
          description: `${reversedStr} reversed due to ${reason}`,
          duration: 6000,
        })
        break
      }

      // ═══════════════════════════════════════════════════════════════════
      // Notification events (in-app notification service)
      // ═══════════════════════════════════════════════════════════════════
      case 'notification.new': {
        queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
        queryClient.invalidateQueries({ queryKey: notificationKeys.all })
        // ServerMessage serializes as { type, payload: { messageId, data: NotificationEventData } }
        const envelope = message.payload as {
          data?: {
            notificationId?: string
            eventType?: string
            title?: string
            message?: string
            payload?: string | null
            timestamp?: number
          }
        }
        const notifData = envelope?.data
        if (notifData?.notificationId && notifData.title) {
          // Parse optional payload JSON into object
          let parsedPayload: Record<string, unknown> | null = null
          if (notifData.payload) {
            try {
              parsedPayload = JSON.parse(notifData.payload) as Record<string, unknown>
            } catch {
              // ignore invalid payload JSON
            }
          }
          useNotificationStore.getState().addNotification({
            id: notifData.notificationId,
            eventType: (notifData.eventType ?? 'system_update') as import('@/types/notification').NotificationEventType,
            title: notifData.title,
            message: notifData.message ?? '',
            channel: 'inapp' as import('@/types/notification').NotificationChannel,
            status: 'delivered' as import('@/types/notification').NotificationStatus,
            readAt: null,
            createdAt: notifData.timestamp
              ? new Date(notifData.timestamp).toISOString()
              : new Date().toISOString(),
            payload: parsedPayload,
          })
          toast.info(notifData.title, {
            description: notifData.message,
            duration: 5000,
          })
        }
        break
      }

      default:
        console.log('Unknown WebSocket message type:', message.type)
    }
  }, [queryClient])

  const connect = React.useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return
    }

    setConnectionState('connecting')

    try {
      const ws = new WebSocket(getWsUrl())
      wsRef.current = ws

      ws.onopen = () => {
        setConnectionState('connected')
        reconnectAttemptsRef.current = 0
        hasShownErrorRef.current = false
        console.log('Connected to real-time updates')

        // Start heartbeat to prevent server-side timeout
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
        }
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping', payload: { timestamp: Date.now() } }))
          }
        }, HEARTBEAT_INTERVAL_MS)
      }

      ws.onclose = () => {
        setConnectionState('disconnected')
        wsRef.current = null
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
          heartbeatIntervalRef.current = null
        }

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++
          const delay = BASE_RECONNECT_MS * Math.pow(2, reconnectAttemptsRef.current - 1)
          console.log(`WebSocket reconnect attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS} in ${delay / 1000}s`)
          // Refresh token before reconnecting to avoid expired token errors
          reconnectTimeoutRef.current = setTimeout(async () => {
            await refreshAccessToken()
            connect()
          }, delay)
        } else {
          console.warn(`WebSocket: max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Use reconnect() to retry manually.`)
        }
      }

      ws.onerror = () => {
        setConnectionState('error')
        if (!hasShownErrorRef.current) {
          hasShownErrorRef.current = true
          console.warn('Real-time connection error — will retry')
        }
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage
          setLastMessage(message)
          handleMessage(message)
        } catch {
          console.error('Failed to parse WebSocket message:', event.data)
        }
      }
    } catch {
      setConnectionState('error')
      // Suppress error log — onerror handler + onclose will handle reconnect
    }
  }, [getWsUrl, handleMessage])

  const send = React.useCallback(<T,>(type: string, payload: T) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage<T> = {
        type,
        payload,
        timestamp: Date.now(),
      }
      wsRef.current.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket is not connected')
    }
  }, [])

  const close = React.useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
    reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS
    wsRef.current?.close()
    wsRef.current = null
    setConnectionState('disconnected')
  }, [])

  const reconnect = React.useCallback(() => {
    close()
    reconnectAttemptsRef.current = 0
    setTimeout(() => connect(), 100)
  }, [close, connect])

  // Connect on mount, disconnect on unmount
  React.useEffect(() => {
    connect()

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      wsRef.current?.close()
      wsRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = React.useMemo<WebSocketContextValue>(() => ({
    connectionState,
    isConnected: connectionState === 'connected',
    lastMessage,
    send,
    close,
    reconnect,
  }), [connectionState, lastMessage, send, close, reconnect])

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}
